import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdmin, canCreateAsset } from "@/lib/roles";
import { fmtDate, fmtHM, fmtTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmployeeGuide from "@/components/EmployeeGuide";
import HomeCompany, { HomeIntro, ContentEditor } from "@/components/HomeCompany";
import { weeklyOffLabel } from "@/lib/weeklyOff";
import {
  Briefcase, CalendarCheck2, Palmtree, Bell, Plus, UserRound, Megaphone, CalendarDays,
  ArrowRight, CheckCircle2, CircleDot, MapPin, Building2, Trash2, Timer, Palette, Smartphone,
  Archive as ArchiveIcon, AlertTriangle, Clock, ChevronRight, GraduationCap, Pencil, Cake,
} from "lucide-react";
import { toast } from "sonner";

const ORANGE = "#e85122";

function istNow() {
  return new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
}
function greeting(d) {
  const h = d.getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

function SectionLabel({ children }) {
  return <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-3">{children}</p>;
}

function MiniStat({ label, value, tone }) {
  return (
    <div className="rounded-xl border border-border bg-card/60 px-3 py-2.5 text-center">
      <p className="font-display font-extrabold text-xl tabular-nums" style={tone ? { color: tone } : {}}>{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

export default function Home() {
  const { user, viewMode } = useAuth();
  const navigate = useNavigate();
  const [now, setNow] = useState(istNow());
  const [assets, setAssets] = useState([]);
  const [leave, setLeave] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [todayLogs, setTodayLogs] = useState([]);
  const [running, setRunning] = useState(null);
  const [posts, setPosts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [anns, setAnns] = useState([]);
  const [team, setTeam] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [weeklyOffs, setWeeklyOffs] = useState([]);
  const [teamRows, setTeamRows] = useState([]);
  const [teamLogs, setTeamLogs] = useState([]);
  const [annOpen, setAnnOpen] = useState(false);
  const [annForm, setAnnForm] = useState({ title: "", body: "", kind: "announcement", event_date: "" });
  const [annEdit, setAnnEdit] = useState(null); // ann_id being edited, null = new
  const [holidayOpen, setHolidayOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [birthdays, setBirthdays] = useState([]);

  const admin = isAdmin(user) && viewMode !== "employee";
  const today = istNow();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  useEffect(() => {
    const t = setInterval(() => setNow(istNow()), 1000);
    return () => clearInterval(t);
  }, []);

  const reload = () => {
    if (!user) return;
    api.get("/assets").then((r) => setAssets(r.data || [])).catch(() => {});
    api.get("/tracker/leave-balance").then((r) => setLeave(r.data)).catch(() => {});
    api.get("/timelogs", { params: { date: todayStr } }).then((r) => setTodayLogs((r.data || []).filter((l) => l.user_id === user.user_id))).catch(() => {});
    api.get("/timelogs/active").then((r) => setRunning(r.data || null)).catch(() => {});
    api.get("/tracker/attendance", { params: { start: todayStr, end: todayStr } }).then((r) => {
      setAttendance((r.data || []).find((x) => x.user_id === user.user_id) || null);
    }).catch(() => {});
    api.get("/posts").then((r) => setPosts((r.data || []).filter((p) => p.owner_id === user.user_id))).catch(() => {});
    api.get("/notifications").then((r) => setUnread(r.data?.unread || 0)).catch(() => {});
    api.get("/announcements").then((r) => setAnns(r.data || [])).catch(() => {});
    api.get("/team").then((r) => setTeam(r.data || [])).catch(() => {});
    api.get("/tracker/holidays").then((r) => setHolidays(r.data || [])).catch(() => {});
    api.get("/tracker/weekly-offs").then((r) => setWeeklyOffs(r.data?.rules || [])).catch(() => {});
    api.get("/content").then((r) => setContent(r.data || null)).catch(() => {});
    api.get("/birthdays/today").then((r) => setBirthdays(r.data || [])).catch(() => {});
    if (user.has_team || admin) {
      api.get("/tracker/attendance", { params: { start: todayStr, end: todayStr } }).then((r) => setTeamRows((r.data || []).filter((x) => x.user_id !== user.user_id))).catch(() => {});
      api.get("/timelogs", { params: { date: todayStr } }).then((r) => setTeamLogs((r.data || []).filter((l) => l.user_id !== user.user_id))).catch(() => {});
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [user]);

  const reportingPerson = useMemo(() => team.find((u) => u.user_id === user?.reporting_to) || null, [team, user]);
  const myReports = useMemo(() => team.filter((u) => u.reporting_to === user?.user_id), [team, user]);
  const teamFeed = useMemo(() => {
    const late = teamRows.filter((r) => r.attendance_status === "LATE").map((r) => ({ id: r.user_id, name: r.user_name, text: `Late check-in${r.first_log_time ? " · " + fmtTime(r.first_log_time) : ""}` }));
    const loggedIds = new Set(teamRows.map((r) => r.user_id));
    const notLogged = myReports.filter((u) => !loggedIds.has(u.user_id)).map((u) => ({ id: u.user_id, name: u.name }));
    const carried = teamLogs.filter((l) => (l.carried_days || 0) > 0 && l.record_type !== "attendance").map((l) => ({ id: l.log_id, name: l.user_name, text: l.activity }));
    return { late, notLogged, carried };
  }, [teamRows, teamLogs, myReports]);

  const myAssets = useMemo(() => assets.filter((a) => (a.work_lead_id || a.owner_id) === user?.user_id), [assets, user]);
  const myActive = myAssets.filter((a) => a.current_stage !== "Complete");
  const myCompleted = myAssets.filter((a) => a.current_stage === "Complete");

  const activities = todayLogs.filter((l) => l.record_type !== "attendance" && l.presence !== "leave");
  const doneToday = activities.filter((l) => l.status === "completed").length;
  const inProgToday = activities.filter((l) => l.status === "in_progress").length;
  const carryToday = activities.filter((l) => (l.carried_days || 0) > 0).length;

  const liveSec = (l) => {
    let s = l?.active_seconds || 0;
    if (l?.is_running && l?.timer_started_at) s += Math.max(0, (now.getTime() - new Date(l.timer_started_at).getTime()) / 1000);
    return s;
  };

  const holidaysByMonth = useMemo(() => {
    const groups = {};
    [...holidays].sort((a, b) => (a.date < b.date ? -1 : 1)).forEach((h) => {
      const d = new Date(h.date + "T00:00:00");
      const key = d.toLocaleString("en-IN", { month: "long", year: "numeric" });
      (groups[key] = groups[key] || []).push({ ...h, d });
    });
    return groups;
  }, [holidays]);
  const nextHoliday = useMemo(() => {
    const t = todayStr;
    return [...holidays].sort((a, b) => (a.date < b.date ? -1 : 1)).find((h) => h.date >= t) || null;
  }, [holidays, todayStr]);
  const holidayToday = useMemo(() => holidays.find((h) => h.date === todayStr) || null, [holidays, todayStr]);
  const weeklyOffToday = useMemo(() => weeklyOffLabel(todayStr, weeklyOffs), [weeklyOffs, todayStr]);
  const offToday = holidayToday?.name || weeklyOffToday || null;

  const attState = !attendance ? (offToday ? "HOLIDAY" : "NOT LOGGED") : attendance.state === "LEAVE" ? "LEAVE" : "PRESENT";
  const attColor = attState === "HOLIDAY" ? ORANGE : attState === "LEAVE" ? "#b45309" : attState === "PRESENT" ? "#059669" : "#64748b";
  const dailyStatus = offToday && !attendance ? "HOLIDAY" : attendance ? "UPDATED" : "NEEDS UPDATE";
  const beforeClose = now.getHours() < 17 || (now.getHours() === 17 && now.getMinutes() < 30);

  const attention = useMemo(() => {
    const items = [];
    if (!attendance && !offToday) items.push({ k: "att", icon: CalendarCheck2, text: "Daily Tracker not logged yet today", to: "/daily-tracker", tone: "#b45309" });
    myActive.forEach((a) => {
      const d = a.due_date || a.deadline;
      if (d) {
        const dd = new Date(d);
        const end = new Date(todayStr + "T23:59:59");
        if (dd < new Date(todayStr + "T00:00:00")) items.push({ k: `ov-${a.asset_id}`, icon: AlertTriangle, text: `Overdue: ${a.work_item || a.title}`, to: "/workflow", tone: "#e11d48" });
        else if (dd <= end) items.push({ k: `due-${a.asset_id}`, icon: Clock, text: `Due today: ${a.work_item || a.title}`, to: "/workflow", tone: ORANGE });
      }
    });
    activities.filter((l) => (l.carried_days || 0) > 0).forEach((l) => items.push({ k: `cf-${l.log_id}`, icon: ArrowRight, text: `Carried forward: ${l.activity}`, to: "/daily-tracker", tone: "#7c3aed" }));
    posts.filter((p) => p.status === "Scheduled" && p.scheduled_at && p.scheduled_at.slice(0, 10) === todayStr).forEach((p) => items.push({ k: `sm-${p.post_id}`, icon: Smartphone, text: `Social due today: ${p.title}`, to: "/calendar", tone: ORANGE }));
    return items;
  }, [attendance, myActive, activities, posts, todayStr, offToday]);

  // Announcements: most recent post/update on top
  const announcements = anns.filter((a) => a.kind !== "upcoming")
    .sort((a, b) => {
      const ta = a.updated_at || a.created_at || "";
      const tb = b.updated_at || b.created_at || "";
      return tb.localeCompare(ta);
    });

  // Upcoming events: chronologically nearest/upcoming on top (or most recently scheduled/updated on top)
  const upcoming = anns.filter((a) => a.kind === "upcoming")
    .sort((a, b) => {
      // If event_date exists, show chronological events (nearest future date first)
      if (a.event_date && b.event_date) return a.event_date.localeCompare(b.event_date);
      if (a.event_date && !b.event_date) return -1;
      if (!a.event_date && b.event_date) return 1;
      const ta = a.updated_at || a.created_at || "";
      const tb = b.updated_at || b.created_at || "";
      return tb.localeCompare(ta);
    });

  const openNewAnn = () => { setAnnEdit(null); setAnnForm({ title: "", body: "", kind: "announcement", event_date: "" }); setAnnOpen(true); };
  const openEditAnn = (a) => { setAnnEdit(a.ann_id); setAnnForm({ title: a.title, body: a.body || "", kind: a.kind === "upcoming" ? "upcoming" : "announcement", event_date: a.event_date || "" }); setAnnOpen(true); };

  const postAnn = async () => {
    if (!annForm.title.trim()) return toast.error("Title is required");
    try {
      if (annEdit) {
        await api.patch(`/announcements/${annEdit}`, { ...annForm, event_date: annForm.event_date || null });
        toast.success("Updated");
      } else {
        await api.post("/announcements", { ...annForm, event_date: annForm.event_date || null });
        toast.success("Posted");
      }
      setAnnOpen(false); setAnnEdit(null); setAnnForm({ title: "", body: "", kind: "announcement", event_date: "" });
      const r = await api.get("/announcements"); setAnns(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const removeAnn = async (id) => {
    try { await api.delete(`/announcements/${id}`); setAnns(anns.filter((a) => a.ann_id !== id)); } catch { toast.error("Could not delete"); }
  };

  const clock = now.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
  const dateLine = `${today.toLocaleString("en-IN", { weekday: "long" })}, ${today.getDate()} ${today.toLocaleString("en-IN", { month: "long" })} ${today.getFullYear()}`;

  const initials = (user?.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

  const quickActions = [
    { label: "Log Activity", icon: Plus, to: "/daily-tracker", testid: "qa-log-activity" },
    { label: "Apply Leave", icon: Palmtree, to: "/daily-tracker", testid: "qa-apply-leave" },
    { label: "My Creative Work", icon: Palette, to: "/workflow", testid: "qa-creative-work" },
    { label: "Social Media", icon: Smartphone, to: "/calendar", testid: "qa-social" },
    { label: "Creative Archive", icon: ArchiveIcon, to: "/archive", testid: "qa-archive" },
    { label: "My Profile", icon: UserRound, to: "/profile", testid: "qa-profile" },
    ...(canCreateAsset(user) ? [{ label: "New Work Item", icon: Briefcase, to: "/workflow", testid: "qa-new-asset" }] : []),
  ];

  const Field = ({ label, value }) => (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold truncate">{value || <span className="text-muted-foreground font-normal">Not assigned</span>}</p>
    </div>
  );

  return (
    <div className="fade-up space-y-8 max-w-6xl" data-testid="home-page">
      {/* 1. Greeting */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="home-greeting">
            {greeting(now)}, {(user?.name || "").split(" ")[0]} <span className="inline-block">👋</span>
          </h2>
          <p className="text-sm text-muted-foreground mt-1.5" data-testid="home-date">{dateLine} · <span className="font-semibold text-foreground tabular-nums" data-testid="home-clock">{clock}</span></p>
          <p className="text-sm text-muted-foreground">Your LiveLife workspace for today.</p>
        </div>
      </div>

      {/* Holiday / weekly-off banner when today is a non-working day */}
      {offToday && (
        <section className="rounded-2xl border-2 p-5 sm:p-6 text-white shadow-md" style={{ background: "linear-gradient(135deg, #e85122, #f0883e)", borderColor: "transparent" }} data-testid="holiday-today-banner">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">🎉 {holidayToday ? "Holiday" : "Weekly Off"}</p>
          <p className="font-display text-2xl font-extrabold mt-1">{offToday}</p>
          <p className="text-sm text-white/90 mt-0.5">{today.toLocaleString("en-IN", { weekday: "long" })}, {fmtDate(todayStr)}</p>
          <p className="text-sm text-white/90 mt-2">Enjoy the day. Your Daily Tracker is not required today.</p>
        </section>
      )}

      {/* Company front door: Welcome, CEO message, Purpose, Values */}
      <HomeIntro content={content} admin={admin} onEdit={() => setEditorOpen(true)} />

      {/* Today's birthdays — celebratory strip, shown only when someone has a birthday */}
      {birthdays.length > 0 && (
        <section data-testid="birthday-banner" className="rounded-2xl px-5 py-4 flex items-center gap-3 text-white shadow-sm" style={{ background: "linear-gradient(135deg, #e85122, #f0883e)" }}>
          <Cake className="h-7 w-7 shrink-0" />
          <div>
            <p className="font-display font-extrabold text-lg leading-tight">Happy Birthday, {birthdays.map((b) => b.name).join(" & ")}!</p>
            <p className="text-xs text-white/85 mt-0.5">Wishing you a fantastic day and a wonderful year ahead — from all of us at LiveLife Education.</p>
          </div>
        </section>
      )}

      {/* Announcements / Upcoming — high on the page in every view */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <SectionLabel>What's Happening at LiveLife</SectionLabel>
          {admin && <Button data-testid="add-announcement-btn" size="sm" variant="outline" className="h-8 text-xs gap-1.5" onClick={openNewAnn}><Megaphone className="h-3.5 w-3.5" /> Post Update</Button>}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="announcements-panel">
            <p className="font-display font-bold mb-3 flex items-center gap-2"><Megaphone className="h-4 w-4" style={{ color: ORANGE }} /> Announcements</p>
            {announcements.length === 0 ? <p className="text-sm text-muted-foreground">No announcements yet.</p> : (
              <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                {announcements.map((a) => (
                  <div key={a.ann_id} className="group flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{a.title}</p>
                      {a.body && <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{a.body}</p>}
                      <p className="text-[10px] text-muted-foreground mt-1">{a.created_by} · {fmtDate(a.created_at)}</p>
                    </div>
                    {admin && (
                      <span className="opacity-0 group-hover:opacity-100 inline-flex shrink-0">
                        <button data-testid={`edit-ann-${a.ann_id}`} onClick={() => openEditAnn(a)} className="p-1 text-muted-foreground hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                        <button data-testid={`del-ann-${a.ann_id}`} onClick={() => removeAnn(a.ann_id)} className="p-1 text-muted-foreground hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="upcoming-panel">
            <p className="font-display font-bold mb-3 flex items-center gap-2"><CalendarDays className="h-4 w-4" style={{ color: ORANGE }} /> Upcoming</p>
            {upcoming.length === 0 ? <p className="text-sm text-muted-foreground">No upcoming events yet.</p> : (
              <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                {upcoming.map((a) => (
                  <div key={a.ann_id} className="group flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="rounded-lg border border-border bg-muted/40 px-2.5 py-1.5 text-center shrink-0">
                        <p className="text-sm font-extrabold leading-none">{a.event_date ? new Date(a.event_date + "T00:00:00").getDate() : "—"}</p>
                        <p className="text-[9px] uppercase tracking-wide text-muted-foreground">{a.event_date ? new Date(a.event_date + "T00:00:00").toLocaleString("en-IN", { month: "short" }) : ""}</p>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate">{a.title}</p>
                        {a.body && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{a.body}</p>}
                        {a.event_date && <p className="text-[10px] text-muted-foreground mt-0.5">Event date: {fmtDate(a.event_date)}</p>}
                      </div>
                    </div>
                    {admin && (
                      <span className="opacity-0 group-hover:opacity-100 inline-flex shrink-0">
                        <button data-testid={`edit-up-${a.ann_id}`} onClick={() => openEditAnn(a)} className="p-1 text-muted-foreground hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                        <button data-testid={`del-up-${a.ann_id}`} onClick={() => removeAnn(a.ann_id)} className="p-1 text-muted-foreground hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 2 + 3. My Profile + Reporting To */}
      <div className="grid lg:grid-cols-3 gap-4">
        <section className="lg:col-span-2 rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-[0_1px_3px_rgba(16,24,40,0.05)]" data-testid="my-profile-card">
          <SectionLabel>My Profile</SectionLabel>
          <div className="flex items-start gap-4">
            {user?.picture
              ? <img src={user.picture} alt="" className="h-16 w-16 rounded-2xl object-cover shrink-0" />
              : <span className="h-16 w-16 rounded-2xl flex items-center justify-center text-white font-display font-extrabold text-xl shrink-0" style={{ background: ORANGE }}>{initials}</span>}
            <div className="min-w-0 flex-1">
              <p className="font-display font-extrabold text-lg truncate">{user?.name}</p>
              <p className="text-sm text-muted-foreground truncate">{user?.designation || "—"}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 mt-4">
                <Field label="Department" value={user?.department} />
                <Field label="Sub-Department" value={user?.sub_department} />
                <Field label="Employee ID" value={user?.employee_id} />
              </div>
            </div>
          </div>
          <button data-testid="view-my-profile" onClick={() => navigate("/profile")} className="mt-4 text-sm font-semibold flex items-center gap-1" style={{ color: ORANGE }}>
            View My Profile <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </section>

        <section className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-[0_1px_3px_rgba(16,24,40,0.05)]" data-testid="reporting-to-card">
          <SectionLabel>Reporting To</SectionLabel>
          {reportingPerson ? (
            <div>
              <div className="flex items-center gap-3">
                {reportingPerson.picture
                  ? <img src={reportingPerson.picture} alt="" className="h-11 w-11 rounded-xl object-cover" />
                  : <span className="h-11 w-11 rounded-xl flex items-center justify-center bg-muted font-display font-bold">{(reportingPerson.name || "?")[0]}</span>}
                <div className="min-w-0">
                  <p className="font-display font-bold truncate">{reportingPerson.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{reportingPerson.designation || "—"}</p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="no-reporting-person">Reporting Person not yet assigned.</p>
          )}
        </section>
      </div>

      {/* 4. Today at LiveLife */}
      <section className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-[0_1px_3px_rgba(16,24,40,0.05)]" data-testid="today-at-livelife">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <SectionLabel>Today at LiveLife</SectionLabel>
          <p className="text-[11px] text-muted-foreground">Office Timing <strong className="text-foreground">9:30 AM – 5:30 PM</strong> · Attendance Cut-off <strong className="text-foreground">10:00 AM</strong></p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <div className="rounded-xl border border-border px-3 py-2.5" data-testid="att-attendance">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Attendance</p>
            <p className="text-sm font-bold mt-0.5" style={{ color: attColor }}>{attState}</p>
          </div>
          <div className="rounded-xl border border-border px-3 py-2.5" data-testid="att-first-log">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">First Log</p>
            <p className="text-sm font-bold mt-0.5">{attendance?.first_log_time ? fmtTime(attendance.first_log_time) : "—"}</p>
          </div>
          <div className="rounded-xl border border-border px-3 py-2.5" data-testid="att-status">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Status</p>
            <p className={`text-sm font-bold mt-0.5 ${attendance?.attendance_status === "LATE" ? "text-rose-600" : attendance?.attendance_status === "ON TIME" ? "text-emerald-600" : ""}`}>{attendance?.attendance_status || "—"}</p>
          </div>
          <div className="rounded-xl border border-border px-3 py-2.5" data-testid="att-location">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Location</p>
            <p className="text-sm font-bold mt-0.5 flex items-center gap-1">{attState === "PRESENT" ? (<>{attendance?.location === "Field" ? <GraduationCap className="h-3.5 w-3.5" /> : <Building2 className="h-3.5 w-3.5" />}{attendance?.location || "Office"}</>) : "—"}</p>
          </div>
          <div className="rounded-xl border border-border px-3 py-2.5" data-testid="att-daily-status">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Daily Status</p>
            <p className={`text-sm font-bold mt-0.5 ${dailyStatus === "UPDATED" ? "text-emerald-600" : "text-amber-600"}`}>{dailyStatus}</p>
          </div>
        </div>
      </section>

      {/* 5. My Work / My Day / My Leave */}
      <div className="grid sm:grid-cols-3 gap-4">
        <section className="rounded-2xl border border-border bg-card p-5" data-testid="my-work-summary">
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" style={{ color: ORANGE }} /> My Work</p>
          <div className="grid grid-cols-3 gap-2 mt-3">
            <MiniStat label="Active" value={myActive.length} />
            <MiniStat label="Completed" value={myCompleted.length} />
            <MiniStat label="In Progress" value={myActive.filter((a) => !["To Do"].includes(a.current_stage)).length} />
          </div>
        </section>
        <section className="rounded-2xl border border-border bg-card p-5" data-testid="my-day-summary">
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><CalendarCheck2 className="h-3.5 w-3.5" style={{ color: ORANGE }} /> My Day</p>
          <div className="grid grid-cols-4 gap-2 mt-3">
            <MiniStat label="Activities" value={activities.length} />
            <MiniStat label="Done" value={doneToday} />
            <MiniStat label="Progress" value={inProgToday} />
            <MiniStat label="Carry" value={carryToday} />
          </div>
        </section>
        <section className="rounded-2xl border border-border bg-card p-5" data-testid="my-leave-summary">
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Palmtree className="h-3.5 w-3.5" style={{ color: ORANGE }} /> My Leave</p>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <MiniStat label="Used" value={leave ? leave.used : "—"} />
            <MiniStat label="Remaining" value={leave ? leave.remaining : "—"} tone={leave && leave.remaining <= 0 ? "#e11d48" : undefined} />
          </div>
          <Button data-testid="apply-leave-btn" variant="outline" size="sm" className="mt-3 w-full h-8 text-xs gap-1.5" onClick={() => navigate("/daily-tracker")}><Palmtree className="h-3.5 w-3.5" /> Apply Leave</Button>
        </section>
      </div>

      {/* 8. Currently working on */}
      {running && (
        <section className="rounded-2xl border-2 p-5 shadow-[0_1px_3px_rgba(16,24,40,0.05)]" style={{ borderColor: `${ORANGE}66`, background: `${ORANGE}0a` }} data-testid="currently-working">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: ORANGE }}>Currently Working On</p>
              <p className="font-display font-extrabold text-lg mt-1 truncate">{running.activity || "Activity"}</p>
              <p className="text-xs text-muted-foreground">{running.task_source === "direct_task" ? `DIRECT TASK · ${running.direct_task_type || ""}` : running.linked_asset_label || "LINKED ASSET"}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 font-mono text-2xl font-extrabold tabular-nums" data-testid="live-timer"><Timer className="h-5 w-5" style={{ color: ORANGE }} />{fmtHM(liveSec(running))}</div>
              <Button data-testid="open-activity-btn" onClick={() => navigate("/daily-tracker")} className="gap-1.5">Open Activity <ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </section>
      )}

      {/* 7. Today's Work + Log Activity */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <SectionLabel>Today's Work</SectionLabel>
          <Button data-testid="home-log-activity" size="sm" className="h-8 gap-1.5" onClick={() => navigate("/daily-tracker")}><Plus className="h-4 w-4" /> Log Activity</Button>
        </div>
        {activities.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="todays-work-empty">
            No activity logged yet today. Click <strong>+ Log Activity</strong> — your first log is also your attendance.
          </div>
        ) : (
          <div className="space-y-2" data-testid="todays-work-list">
            {activities.map((l) => (
              <div key={l.log_id} className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3">
                {l.status === "completed" ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <CircleDot className="h-5 w-5 shrink-0" style={{ color: ORANGE }} />}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{l.activity || "Activity"}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {l.task_source === "direct_task" ? `DIRECT TASK${l.direct_task_type ? " · " + l.direct_task_type : ""}` : (l.linked_asset_label ? `LINKED ASSET · ${l.linked_asset_label}` : "LINKED ASSET")}
                    {(l.carried_days || 0) > 0 && <span className="ml-2 text-violet-600 font-semibold">Carried Forward: {l.carried_days} day{l.carried_days > 1 ? "s" : ""}</span>}
                  </p>
                </div>
                <span className="font-mono text-xs text-muted-foreground shrink-0">{fmtHM(liveSec(l))}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wider shrink-0 ${l.status === "completed" ? "text-emerald-600" : "text-amber-600"}`}>{l.status === "completed" ? "Completed" : "In Progress"}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 13. My Attention */}
      <section data-testid="my-attention">
        <SectionLabel>My Attention</SectionLabel>
        {attention.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">You're all caught up. Nothing needs your attention right now. 🎉</div>
        ) : (
          <div className="space-y-2">
            {attention.map((it) => {
              const Icon = it.icon;
              return (
                <button key={it.k} data-testid={`attention-${it.k}`} onClick={() => navigate(it.to)} className="w-full text-left flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 hover:border-primary/30 transition-colors">
                  <Icon className="h-4.5 w-4.5 shrink-0" style={{ color: it.tone }} />
                  <span className="text-sm font-medium flex-1 min-w-0 truncate">{it.text}</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Team Attention Feed (Reporting Persons / Admin) */}
      {(user?.has_team || admin) && (
        <section data-testid="team-attention">
          <SectionLabel>Team Attention</SectionLabel>
          {teamFeed.late.length === 0 && teamFeed.notLogged.length === 0 && teamFeed.carried.length === 0 ? (
            <div className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">Your team is on track today — no late check-ins or carried-forward work.</div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="rounded-2xl border border-border bg-card p-4" data-testid="team-late">
                <p className="text-[10px] font-bold uppercase tracking-widest text-rose-600">Late Check-ins ({teamFeed.late.length})</p>
                <div className="mt-2 space-y-1.5">{teamFeed.late.length === 0 ? <p className="text-xs text-muted-foreground">None</p> : teamFeed.late.map((x) => <p key={x.id} className="text-sm"><span className="font-medium">{x.name}</span> <span className="text-xs text-muted-foreground">· {x.text}</span></p>)}</div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-4" data-testid="team-notlogged">
                <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Not Logged ({teamFeed.notLogged.length})</p>
                <div className="mt-2 space-y-1.5">{teamFeed.notLogged.length === 0 ? <p className="text-xs text-muted-foreground">Everyone logged in</p> : teamFeed.notLogged.map((x) => <p key={x.id} className="text-sm font-medium">{x.name}</p>)}</div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-4" data-testid="team-carried">
                <p className="text-[10px] font-bold uppercase tracking-widest text-violet-600">Carried Forward ({teamFeed.carried.length})</p>
                <div className="mt-2 space-y-1.5">{teamFeed.carried.length === 0 ? <p className="text-xs text-muted-foreground">None</p> : teamFeed.carried.map((x) => <p key={x.id} className="text-sm"><span className="font-medium">{x.name}</span> <span className="text-xs text-muted-foreground">· {x.text}</span></p>)}</div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* 10. My Creative Work */}
      <section data-testid="my-creative-work">
        <div className="flex items-center justify-between mb-3">
          <SectionLabel>My Creative Work</SectionLabel>
          <button data-testid="view-creative-work" onClick={() => navigate("/workflow")} className="text-xs font-semibold flex items-center gap-1" style={{ color: ORANGE }}>View all <ArrowRight className="h-3 w-3" /></button>
        </div>
        {myActive.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-5 text-center text-sm text-muted-foreground">No creative work currently assigned to you.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {myActive.slice(0, 6).map((a) => (
              <button key={a.asset_id} onClick={() => navigate("/workflow")} className="text-left rounded-xl border border-border bg-card px-4 py-3 hover:border-primary/30 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium truncate">{a.work_item || a.title}</p>
                  {(a.priority === "High" || a.priority === "Critical") && <span className="rounded-full bg-rose-500/10 text-rose-600 text-[10px] font-bold px-2 py-0.5 shrink-0">{a.priority}</span>}
                </div>
                <div className="flex items-center gap-2 mt-1.5 text-[11px] text-muted-foreground">
                  <span className="rounded-full bg-muted px-2 py-0.5 font-semibold uppercase tracking-wide">{a.current_stage}</span>
                  {(a.due_date || a.deadline) && <span>Due {fmtDate(a.due_date || a.deadline)}</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* 12. My Social Media */}
      <section data-testid="my-social-media">
        <div className="flex items-center justify-between mb-3">
          <SectionLabel>My Social Media</SectionLabel>
          <button data-testid="open-social" onClick={() => navigate("/calendar")} className="text-xs font-semibold flex items-center gap-1" style={{ color: ORANGE }}>Open Social Media <ArrowRight className="h-3 w-3" /></button>
        </div>
        {posts.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-5 text-center text-sm text-muted-foreground" data-testid="social-empty">No Social Media content currently assigned to you.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {posts.slice(0, 4).map((p) => {
              const overdue = p.status === "Scheduled" && p.scheduled_at && new Date(p.scheduled_at) < new Date();
              return (
                <div key={p.post_id} className="rounded-xl border border-border bg-card px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium truncate">{p.title}</p>
                    {overdue && <span className="rounded-full bg-rose-500/10 text-rose-600 text-[10px] font-bold px-2 py-0.5 shrink-0">Overdue</span>}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">{p.platform} · {p.content_type} · <span className="font-semibold">{p.status}</span>{p.scheduled_at ? ` · ${fmtDate(p.scheduled_at)}` : ""}</p>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* 10/6. Quick Actions */}
      <section>
        <SectionLabel>Quick Actions</SectionLabel>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {quickActions.map((a) => (
            <button key={a.testid} data-testid={a.testid} onClick={() => navigate(a.to)} className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3 hover:border-primary/40 hover:shadow-md transition-all text-left">
              <span className="h-9 w-9 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: ORANGE }}><a.icon className="h-4 w-4" /></span>
              <span className="text-sm font-semibold">{a.label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* 15. Day / Week / Month */}
      <section>
        <SectionLabel>My History</SectionLabel>
        <div className="grid grid-cols-3 gap-3">
          {[["Day", "Today's attendance & activities"], ["Week", "Mon–Sun summary"], ["Month", "Monthly attendance & leave"]].map(([k, sub]) => (
            <button key={k} data-testid={`history-${k.toLowerCase()}`} onClick={() => navigate("/daily-tracker")} className="rounded-2xl border border-border bg-card p-4 text-left hover:border-primary/40 hover:shadow-md transition-all">
              <p className="font-display font-extrabold flex items-center gap-2"><CalendarDays className="h-4 w-4" style={{ color: ORANGE }} />{k}</p>
              <p className="text-[11px] text-muted-foreground mt-1">{sub}</p>
            </button>
          ))}
        </div>
      </section>

      {/* 14. My Day Status */}
      <section className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-[0_1px_3px_rgba(16,24,40,0.05)]" data-testid="day-status">
        <p className="font-display font-bold mb-3">Today's Status</p>
        <div className="space-y-1.5 text-sm">
          <p className={attendance ? "text-emerald-600" : "text-muted-foreground"}>{attendance ? "✓" : "○"} Attendance {attendance ? "recorded" : "not logged"}</p>
          <p>{activities.length > 0 ? "✓" : "○"} {activities.length} activit{activities.length === 1 ? "y" : "ies"} logged</p>
          <p className="text-emerald-600">✓ {doneToday} activit{doneToday === 1 ? "y" : "ies"} completed</p>
          {inProgToday > 0 && <p className="text-amber-600">⚠ {inProgToday} activit{inProgToday === 1 ? "y" : "ies"} in progress</p>}
          {carryToday > 0 && <p className="text-violet-600">↪ {carryToday} activit{carryToday === 1 ? "y" : "ies"} carried forward</p>}
        </div>
        {dailyStatus === "NEEDS UPDATE" && beforeClose && (
          <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50/60 dark:bg-amber-950/30 p-3" data-testid="daily-update-needed">
            <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">Daily Update Needed</p>
            <p className="text-[12px] text-muted-foreground mt-0.5">Please review your Daily Tracker before 5:30 PM.</p>
          </div>
        )}
      </section>

      {/* Holidays & Important Days (compact card) */}
      <section className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-[0_1px_3px_rgba(16,24,40,0.05)]" data-testid="holidays-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="font-display font-extrabold text-lg flex items-center gap-2">🎉 Holidays & Important Days</p>
            <p className="text-xs text-muted-foreground mt-0.5">Plan ahead. Stay informed.</p>
            <p className="text-[13px] text-muted-foreground mt-2 max-w-md leading-relaxed">Here are the LiveLife Education holidays and important days for {new Date().getFullYear()}. Use this calendar to plan your work, leave and commitments in advance.</p>
          </div>
          {nextHoliday && (
            <div className="rounded-xl border border-border bg-muted/30 px-4 py-3 min-w-[180px]" data-testid="next-holiday">
              <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: ORANGE }}>Upcoming Holiday</p>
              <p className="font-display font-extrabold text-base mt-1">{nextHoliday.name}</p>
              <p className="text-xs text-muted-foreground">{fmtDate(nextHoliday.date)}</p>
              <p className="text-[11px] text-muted-foreground">{new Date(nextHoliday.date + "T00:00:00").toLocaleString("en-IN", { weekday: "long" })}</p>
            </div>
          )}
        </div>
        <button data-testid="view-holiday-calendar" onClick={() => setHolidayOpen(true)} className="mt-4 text-sm font-semibold flex items-center gap-1" style={{ color: ORANGE }}>
          View {holidays.length ? new Date(holidays[0].date + "T00:00:00").getFullYear() : new Date().getFullYear()} Holiday Calendar <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </section>

      {/* 16. Employee Guide */}
      <EmployeeGuide user={user} reportingName={reportingPerson?.name} />

      {/* Company: Policies, Our People, Process / Progress / Practice */}
      <HomeCompany content={content} admin={admin} team={team} upcoming={upcoming}
        myActiveCount={myActive.length} myCompletedCount={myCompleted.length} onEdit={() => setEditorOpen(true)} />

      <ContentEditor content={content} open={editorOpen} onClose={() => setEditorOpen(false)} onSaved={reload} />

      {/* Full Holiday Calendar modal */}
      <Dialog open={holidayOpen} onOpenChange={setHolidayOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[85vh] overflow-y-auto p-0" data-testid="holiday-calendar-modal">
          <div className="p-5 sm:p-6 text-white rounded-t-lg" style={{ background: "linear-gradient(135deg, #e85122, #f0883e)" }}>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/80">LiveLife Education</p>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl sm:text-3xl font-extrabold text-white">Holiday List {holidays.length ? new Date(holidays[0].date + "T00:00:00").getFullYear() : new Date().getFullYear()}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-white/90 mt-1">Plan ahead. Stay informed.</p>
          </div>
          <div className="p-5 sm:p-6">
            {Object.keys(holidaysByMonth).length === 0 ? (
              <p className="text-sm text-muted-foreground">No holidays published yet.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
                {Object.entries(holidaysByMonth).map(([month, list]) => (
                  <div key={month}>
                    <p className="font-display font-extrabold text-sm uppercase tracking-wide mb-2" style={{ color: ORANGE }}>{month}</p>
                    <div className="space-y-1.5">
                      {list.map((h) => {
                        const isNext = nextHoliday && h.date === nextHoliday.date;
                        return (
                          <div key={h.date} className={`flex items-baseline gap-2 text-sm ${isNext ? "font-semibold" : ""}`} data-testid={`holiday-${h.date}`}>
                            <span className="text-muted-foreground tabular-nums w-24 shrink-0">{h.d.toLocaleString("en-IN", { month: "short", day: "2-digit" })}, {h.d.toLocaleString("en-IN", { weekday: "short" })}</span>
                            <span className="text-muted-foreground">—</span>
                            <span className="flex items-center gap-1.5">{h.name}{isNext && <span className="rounded-full text-[9px] font-bold px-1.5 py-0.5 text-white" style={{ background: ORANGE }}>NEXT</span>}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[10px] text-muted-foreground mt-6 text-right">LLE/HR/R/HolidayList/V1/{holidays.length ? new Date(holidays[0].date + "T00:00:00").getFullYear() : new Date().getFullYear()}</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin: post / edit announcement */}
      <Dialog open={annOpen} onOpenChange={(o) => { setAnnOpen(o); if (!o) setAnnEdit(null); }}>
        <DialogContent aria-describedby={undefined} className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">{annEdit ? "Edit Update" : "Post an Update"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={annForm.kind} onValueChange={(v) => setAnnForm({ ...annForm, kind: v })}>
              <SelectTrigger data-testid="ann-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="announcement">Announcement</SelectItem>
                <SelectItem value="upcoming">Upcoming event</SelectItem>
              </SelectContent>
            </Select>
            <Input data-testid="ann-title" placeholder="Title" value={annForm.title} onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })} />
            <Textarea data-testid="ann-body" placeholder="Details (optional)" rows={3} value={annForm.body} onChange={(e) => setAnnForm({ ...annForm, body: e.target.value })} />
            {annForm.kind === "upcoming" && <Input data-testid="ann-date" type="date" value={annForm.event_date} onChange={(e) => setAnnForm({ ...annForm, event_date: e.target.value })} />}
            <div className="flex justify-end"><Button data-testid="ann-submit" onClick={postAnn}>{annEdit ? "Save" : "Post"}</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
